import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Plus, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function MeetingsListPage() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [type, setType] = useState("committee");
  const [agenda, setAgenda] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => { const r = await api.get("/meetings"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/meetings", { title, date: new Date(date).toISOString(), type, agenda, raw_notes: notes });
      toast.success("Meeting saved");
      setCreating(false); setTitle(""); setAgenda(""); setNotes("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-4xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Meetings</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Building meetings</h1>
        </div>
        <Button onClick={() => setCreating(!creating)} data-testid="new-meeting-btn"
          className="rounded-full bg-[#C05640] hover:bg-[#A54734]"><Plus size={18} className="mr-1" /> New meeting</Button>
      </div>

      {creating && (
        <form onSubmit={create} className="mt-5 bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid="meeting-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Title</Label><Input value={title} required onChange={(e)=>setTitle(e.target.value)} data-testid="m-title" className="h-11 mt-1" /></div>
            <div><Label>Date</Label><Input type="date" value={date} required onChange={(e)=>setDate(e.target.value)} data-testid="m-date" className="h-11 mt-1" /></div>
          </div>
          <div>
            <Label>Type</Label>
            <select value={type} onChange={(e)=>setType(e.target.value)} className="w-full h-11 mt-1 rounded-xl border border-stone-300 px-3" data-testid="m-type">
              <option value="committee">Committee</option>
              <option value="general_body">General body</option>
              <option value="informal">Informal</option>
            </select>
          </div>
          <div><Label>Agenda</Label><Textarea rows={2} value={agenda} onChange={(e)=>setAgenda(e.target.value)} data-testid="m-agenda" className="mt-1" /></div>
          <div><Label>Raw notes (we'll structure them with AI)</Label><Textarea rows={5} value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="m-notes" className="mt-1" /></div>
          <Button type="submit" data-testid="m-save" className="rounded-full bg-stone-900 text-white">Save meeting</Button>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {items.map((m) => (
          <Link key={m.id} to={`/meetings/${m.id}`} data-testid={`mt-${m.id}`}
            className="flex items-start gap-4 bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-400 transition">
            <div className="w-12 h-12 rounded-xl bg-[#F4F1EA] grid place-items-center text-[#C05640] shrink-0"><Calendar size={20}/></div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-stone-500 uppercase tracking-wider">{m.type.replace("_", " ")}</div>
              <div className="font-medium text-stone-900">{m.title}</div>
              <div className="text-xs text-stone-500 mt-0.5">{fmtDate(m.date)} · {m.attendees?.length || 0} attendees</div>
              {m.ai_summary?.action_items?.length > 0 && (
                <div className="text-xs text-[#C05640] mt-1 font-medium">{m.ai_summary.action_items.length} action items</div>
              )}
            </div>
          </Link>
        ))}
        {items.length === 0 && <div className="text-stone-500 italic py-8 text-center">No meetings yet — start by recording your last committee call.</div>}
      </div>
    </div>
  );
}
